import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';

const { Bug, Sparkle, Calendar, ChatCircle, CheckCircle, Clock, HourglassMedium, ShieldCheck } = PhosphorIcons;

const TYPE_META = {
  bug: { label: 'Bug', Icon: Bug, color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  feature: { label: 'Feature', Icon: Sparkle, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200' },
  demo: { label: 'Demo', Icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-50 border-purple-200' },
  other: { label: 'Other', Icon: ChatCircle, color: 'text-zinc-500', bg: 'bg-zinc-50 border-zinc-200' },
};

const STATUS_META = {
  open: { label: 'Open', Icon: Clock, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  in_progress: { label: 'In Progress', Icon: HourglassMedium, color: 'text-blue-700 bg-blue-50 border-blue-200' },
  resolved: { label: 'Resolved', Icon: CheckCircle, color: 'text-green-700 bg-green-50 border-green-200' },
};

const AdminFeedback = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);
  const [response, setResponse] = useState('');
  const [nextStatus, setNextStatus] = useState('in_progress');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const [list, s] = await Promise.all([
        apiClient.get(`/admin/feedback${params}`),
        apiClient.get('/admin/feedback/stats'),
      ]);
      setItems(list.data);
      setStats(s.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const openReply = (f) => {
    setActive(f);
    setResponse(f.admin_response || '');
    setNextStatus(f.status === 'resolved' ? 'resolved' : 'in_progress');
  };

  const saveReply = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await apiClient.patch(`/admin/feedback/${active.id}`, {
        status: nextStatus,
        admin_response: response,
      });
      toast.success('Feedback updated');
      setActive(null);
      setResponse('');
      fetchAll();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };

  if (user?.role !== 'global_super_admin') {
    return <div className="text-red-500 font-mono text-center p-8" data-testid="feedback-admin-denied">Access denied.</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="admin-feedback-title">
          <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Feedback Inbox</span>
        </h1>
        <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase flex items-center gap-2">
          <ShieldCheck size={14} /> All tenants · All types
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} color="border-blue-500/20" />
          <StatCard label="Open" value={stats.open} color="border-amber-500/30" />
          <StatCard label="In Progress" value={stats.in_progress} color="border-blue-500/20" />
          <StatCard label="Resolved" value={stats.resolved} color="border-green-500/20" />
        </div>
      )}

      <div className="flex items-center gap-3">
        <span className="text-sm font-mono text-[#6b665c]">FILTER:</span>
        {['all', 'open', 'in_progress', 'resolved'].map((s) => (
          <Button
            key={s}
            onClick={() => setFilter(s)}
            className={`h-8 px-3 text-xs font-mono border ${filter === s ? 'bg-amber-600 text-white border-amber-700' : 'bg-white hover:bg-[#e4ebe5] text-[#5a6e5f] border-[#c5d4c8]'}`}
            data-testid={`filter-${s}`}
          >
            {s.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#6b665c] font-mono">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#c5d4c8] rounded-md text-center py-12" data-testid="admin-feedback-empty">
          <ChatCircle size={48} className="text-[#a39e92] mx-auto mb-4" />
          <p className="text-[#8a8477] font-mono">No feedback matching this filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((f) => {
            const meta = TYPE_META[f.type] || TYPE_META.other;
            const status = STATUS_META[f.status] || STATUS_META.open;
            return (
              <div
                key={f.id}
                className="bg-white border border-[#c5d4c8] rounded-md p-5"
                data-testid={`admin-feedback-item-${f.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${meta.bg}`}>
                      <meta.Icon size={20} className={meta.color} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-sm mb-0.5 truncate">{f.subject}</h3>
                      <p className="text-xs text-[#8a8477] font-mono">
                        <span className="text-[#2d2a26] font-medium">{f.tenant_company_name || 'Unknown tenant'}</span>
                        {' · '}{f.user_email}{' · '}{new Date(f.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-mono border flex items-center gap-1 ${status.color}`}>
                    <status.Icon size={12} />
                    {status.label}
                  </span>
                </div>
                <p className="text-sm text-[#2d2a26] whitespace-pre-wrap mb-3">{f.message}</p>
                {f.admin_response && (
                  <div className="bg-[#f5f0e8] border-l-2 border-[#7c9082] p-3 rounded mb-3">
                    <p className="text-xs text-[#5a6e5f] font-mono uppercase tracking-wider mb-1">Last response</p>
                    <p className="text-sm text-[#2d2a26] whitespace-pre-wrap">{f.admin_response}</p>
                  </div>
                )}
                <Button
                  onClick={() => openReply(f)}
                  className="bg-amber-600 hover:bg-amber-700 text-white border-0 h-8 text-xs"
                  data-testid={`reply-${f.id}`}
                >
                  Reply / Update status
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Reply dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setResponse(''); } }}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-mono">Respond to feedback</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="bg-[#f5f0e8] border border-[#c5d4c8] rounded p-3 text-sm">
                <p className="font-bold mb-1">{active.subject}</p>
                <p className="text-xs text-[#8a8477] font-mono mb-2">{active.tenant_company_name} · {active.user_email}</p>
                <p className="whitespace-pre-wrap text-[#2d2a26]">{active.message}</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5a6e5f] font-mono">Status</label>
                <Select value={nextStatus} onValueChange={setNextStatus}>
                  <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8]" data-testid="reply-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5a6e5f] font-mono">Response</label>
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  maxLength={4000}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] min-h-[140px]"
                  placeholder="Write a response the tenant will see…"
                  data-testid="reply-response-input"
                />
              </div>
              <Button onClick={saveReply} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-reply-btn">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className={`bg-white border ${color} rounded-md p-5`}>
    <p className="text-2xl font-bold font-mono">{value}</p>
    <p className="text-xs text-[#8a8477] font-mono uppercase tracking-wider mt-1">{label}</p>
  </div>
);

export default AdminFeedback;
